import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import {
  Callout,
  EmptyState,
  Figure,
  IconChevronLeft,
  IconRefresh,
  ListPage,
  Pagination,
  Segmented,
  Skeleton,
  Spinner,
  StatusPill,
  Table,
  TableSkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
  When,
} from '../../ui/v2';
import {
  fetchRefundOrders,
  filterRefunds,
  REFUND_STATUS_LABEL,
  REFUND_STATUS_VIEWS,
  refundStatusTone,
  refundTotals,
  REFUNDS_PAGE_SIZE,
  type RefundSortKey,
  type RefundStatusView,
  sortRefunds,
} from './refundModel';

const COLUMNS: {
  key: string;
  label: string;
  width?: number;
  align?: 'left' | 'right';
  sortKey?: RefundSortKey;
}[] = [
  { key: 'order_id', label: 'Order', width: 80, sortKey: 'order_id' },
  { key: 'customer_name', label: 'Customer' },
  {
    key: 'customer_id',
    label: 'Customer ID',
    width: 104,
    sortKey: 'customer_id',
  },
  { key: 'created_at', label: 'Date', width: 144, sortKey: 'created_at' },
  {
    key: 'total_price',
    label: 'Order total',
    width: 104,
    align: 'right',
    sortKey: 'total_price',
  },
  {
    key: 'amount',
    label: 'Amount refunded',
    width: 144,
    align: 'right',
    sortKey: 'amount',
  },
  { key: 'status', label: 'Status', width: 176 },
  { key: 'reason', label: 'Reason', width: 80 },
];

const STATUS_ITEMS = REFUND_STATUS_VIEWS.map((key) => ({
  key,
  label: REFUND_STATUS_LABEL[key],
}));

const formatDollars = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(n);

const Placeholder: React.FC<{ value: React.ReactNode }> = ({ value }) =>
  value === '-' || value == null ? (
    <span className="text-v2-fg-3">—</span>
  ) : (
    <>{value}</>
  );

export default function RefundsTable() {
  const [view, setView] = useState<RefundStatusView>('All');
  const [sortKey, setSortKey] = useState<RefundSortKey>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isPending, isError, error, isFetching } = useQuery({
    queryKey: useScopedQueryKey('refunds'),
    queryFn: fetchRefundOrders,
  });

  const refunds = useMemo(() => data ?? [], [data]);
  const showInitialLoading = isPending && refunds.length === 0;
  const fetchError =
    isError && error instanceof Error
      ? error.message
      : isError
        ? 'Failed to load orders.'
        : null;

  const filtered = useMemo(() => filterRefunds(refunds, view), [refunds, view]);
  const sorted = useMemo(
    () => sortRefunds(filtered, sortKey, sortOrder),
    [filtered, sortKey, sortOrder],
  );
  const pageRows = sorted.slice(
    (currentPage - 1) * REFUNDS_PAGE_SIZE,
    currentPage * REFUNDS_PAGE_SIZE,
  );
  const totalPages = Math.max(1, Math.ceil(sorted.length / REFUNDS_PAGE_SIZE));
  const { refundCount, refundedAmountTotal } = refundTotals(filtered);

  const isRefreshing =
    isFetching && !showInitialLoading && !fetchError && pageRows.length > 0;
  const showCount = showInitialLoading || refundCount > 0;
  const showAmount = showInitialLoading || refundedAmountTotal > 0;

  const handleViewChange = (next: RefundStatusView) => {
    setView(next);
    setCurrentPage(1);
  };

  const handleSort = (key: RefundSortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  return (
    <>
      <PageMeta
        title="AskArthur | Refunds"
        description="AskArthur Refunds Page"
      />

      <ListPage
        title="Refunds"
        actions={
          <>
            {isRefreshing && (
              <span
                role="status"
                className="inline-flex items-center gap-1.5 text-v2-12 text-v2-fg-3"
              >
                <Spinner />
                Updating refunds...
              </span>
            )}
            <Link
              to="/orders"
              className="inline-flex h-8 items-center gap-1 rounded-v2 px-2 text-v2-13 font-medium text-v2-accent hover:underline"
            >
              <IconChevronLeft size={14} />
              Back to Orders
            </Link>
          </>
        }
        toolbar={
          <div className="flex min-h-11 flex-wrap items-center gap-x-6 gap-y-2 px-4 py-2 lg:flex-nowrap lg:px-6 lg:py-0">
            <Segmented
              aria-label="Refund status"
              items={STATUS_ITEMS}
              value={view}
              onChange={handleViewChange}
              className="shrink-0"
            />
            {showCount && (
              <Figure
                label="refund cases"
                className="hidden shrink-0 md:flex"
                value={
                  showInitialLoading ? (
                    <Skeleton width={32} />
                  ) : (
                    refundCount.toLocaleString()
                  )
                }
              />
            )}
            {showAmount && (
              <Figure
                label="refunded"
                className="hidden shrink-0 md:flex"
                value={
                  showInitialLoading ? (
                    <Skeleton width={56} />
                  ) : (
                    formatDollars(refundedAmountTotal)
                  )
                }
              />
            )}
          </div>
        }
      >
        {fetchError ? (
          <div className="p-4 lg:p-6">
            <Callout tone="danger" title="Couldn't load refunds">
              {fetchError}
            </Callout>
          </div>
        ) : !showInitialLoading && pageRows.length === 0 ? (
          <EmptyState
            icon={<IconRefresh />}
            title="You don't have any refunds at the moment"
            description="When refunds appear, they'll show up here."
          />
        ) : (
          <>
            {showInitialLoading && (
              <p role="status" className="sr-only">
                Loading refunds…
              </p>
            )}
            <div
              className={clsx(
                'v2-scroll overflow-x-auto transition-opacity',
                isRefreshing && 'opacity-70',
              )}
            >
              <Table fixed className="min-w-[960px]">
                <THead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <TH
                        key={column.key}
                        width={column.width}
                        align={column.align}
                        sortable={
                          Boolean(column.sortKey) && !showInitialLoading
                        }
                        active={
                          Boolean(column.sortKey) && sortKey === column.sortKey
                        }
                        dir={sortOrder}
                        onSort={() =>
                          column.sortKey && handleSort(column.sortKey)
                        }
                      >
                        {column.label}
                      </TH>
                    ))}
                  </tr>
                </THead>
                <TBody>
                  {showInitialLoading ? (
                    <TableSkeletonRows cols={COLUMNS.length} />
                  ) : (
                    pageRows.map((refund) => (
                      <TR key={refund.id}>
                        <TD num className="font-medium">
                          #{refund.order_id}
                        </TD>
                        <TD truncate className="font-medium">
                          <Placeholder value={refund.customer_name} />
                        </TD>
                        <TD num className="text-v2-fg-2">
                          <Placeholder value={refund.customer_id} />
                        </TD>
                        <TD num className="whitespace-nowrap">
                          <When value={refund.created_at} />
                        </TD>
                        <TD num align="right">
                          {refund.total_price}
                        </TD>
                        <TD num align="right" className="font-medium">
                          {refund.amount}
                        </TD>
                        <TD>
                          <StatusPill
                            tone={refundStatusTone(refund.refund_status)}
                          >
                            {REFUND_STATUS_LABEL[
                              refund.refund_status as RefundStatusView
                            ] ?? refund.refund_status}
                          </StatusPill>
                        </TD>
                        <TD>
                          <Placeholder value={refund.reason} />
                        </TD>
                      </TR>
                    ))
                  )}
                </TBody>
              </Table>
            </div>
            {!showInitialLoading && (
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                totalCount={sorted.length}
                pageSize={REFUNDS_PAGE_SIZE}
                onPage={setCurrentPage}
                noun="refunds"
              />
            )}
          </>
        )}
      </ListPage>
    </>
  );
}
